import { useCallback } from 'react'
import type { SelectFromFilterRequest } from '../../types/case-container'
import { clearSelection, selectFromFilter } from '../../services/selections'
import { logger } from '../../utils/logger'
import type { SelectionFetchState } from './use-selection-fetch'
import type { PendingSyncControl } from './use-pending-sync'

export interface SelectionBulk {
  selectAllFiltered: (filter: SelectFromFilterRequest) => Promise<void>
  clearAll: () => Promise<void>
}

export function useSelectionBulk(
  caseId: string | null,
  fetchState: SelectionFetchState,
  pendingSync: PendingSyncControl
): SelectionBulk {
  const { setSelectedIds, setError } = fetchState
  const { setSyncing, cancelPendingTimer, clearPending } = pendingSync

  const selectAllFiltered = useCallback(
    async (filter: SelectFromFilterRequest) => {
      if (!caseId) return
      setSyncing(true)
      setError(null)
      try {
        const result = await selectFromFilter(caseId, filter)
        setSelectedIds(new Set(result.selectedEndpointIds))
        clearPending()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to select filtered items')
      } finally {
        setSyncing(false)
      }
    },
    [caseId, setSelectedIds, setError, setSyncing, clearPending]
  )

  const clearAll = useCallback(async () => {
    if (!caseId) return
    setSelectedIds(new Set())
    cancelPendingTimer()
    clearPending()
    setSyncing(true)
    setError(null)
    try {
      await clearSelection(caseId)
    } catch (err) {
      logger.warn('useSelection', 'Failed to sync clear selection to backend', err)
      setError(err instanceof Error ? err.message : 'Failed to clear selection')
    } finally {
      setSyncing(false)
    }
  }, [caseId, setSelectedIds, setError, setSyncing, cancelPendingTimer, clearPending])

  return { selectAllFiltered, clearAll }
}
